import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';

// Expected top-level fields to check for presence
const EXPECTED_FIELDS = ['email', 'name', 'status', 'device_type', 'visited_page', 'local_time'];

/**
 * The UI renders this value straight into the error banner, so it has to be a
 * string. An axios failure carries a JSON body, which used to reach the banner
 * as an object and render as "[object Object]".
 *
 * @param {unknown} error
 * @returns {string}
 */
export const toUserTrafficErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof error === 'string' && error.trim()) return error.trim();
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return 'Request failed';
};

export const getUserTrafficList = createAsyncThunk(
  'userTraffic/getUserTrafficList',
  async ({ businessId, page = 1, limit = 10, groupId } = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authToken = state?.loginToken?.response?.token || state?.loginTokenCoUser?.response?.token;
      const resolvedBusinessId = businessId || state?.loginToken?.response?.user?.business_id || state?.loginTokenCoUser?.response?.user?.business_id;

      if (!resolvedBusinessId) {
        return rejectWithValue('Missing business_id');
      }

      if (!groupId) {
        return rejectWithValue('Missing group_id');
      }

      const url = `${ROOT_URL}/co-users/user-traffic/${resolvedBusinessId}`;
      const response = await axios.get(url, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
        params: { group_id: groupId, page, limit },
      });

      const raw = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      // Enrich each record with missingFields array
      const data = raw.map((item) => {
        const missingFields = EXPECTED_FIELDS.filter((field) => {
          const value = item?.[field];
          return value === undefined || value === null || value === '';
        });
        return { ...item, missingFields };
      });

      const pagination = response?.data?.pagination || {
        page,
        limit,
        total: Array.isArray(raw) ? raw.length : 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      };

      return { data, pagination };
    } catch (error) {
      return rejectWithValue(toUserTrafficErrorMessage(error));
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  data: [],
  lastFetchedAt: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
};

const enrichRecord = (item) => {
  const missingFields = EXPECTED_FIELDS.filter((field) => {
    const value = item?.[field];
    return value === undefined || value === null || value === '';
  });
  return { ...item, missingFields };
};

const userTrafficSlice = createSlice({
  name: 'userTraffic',
  initialState,
  reducers: {
    resetUserTrafficState: (state) => {
      state.loading = false;
      state.error = null;
      state.data = [];
      state.lastFetchedAt = null;
      state.pagination = { page: 1, limit: 10, total: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false };
    },
    upsertVisitor: (state, action) => {
      const payload = action.payload || {};
      const id = payload?._id || payload?.id;
      if (!id) return;
      if ((payload?.status || '').toLowerCase() === 'left') {
        const initialLength = state.data.length;
        state.data = state.data.filter((v) => (v?._id || v?.id) !== id);
        if (state.data.length < initialLength && state.pagination.total > 0) {
          state.pagination.total -= 1;
        }
      } else {
        const idx = state.data.findIndex((v) => (v?._id || v?.id) === id);
        if (idx >= 0) {
          // Socket updates only carry what changed, so fields the server omitted
          // arrive as `undefined` and must not erase the stored value. An explicit
          // `null` still clears, so ending a chat can drop `currentRoom`.
          const merged = { ...state.data[idx] };
          Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined) merged[key] = value;
          });
          state.data[idx] = enrichRecord(merged);
        } else {
          state.data.unshift(enrichRecord(payload));
          state.pagination.total += 1;
        }
      }
    },
    removeVisitor: (state, action) => {
      const id = action.payload?._id || action.payload?.id;
      if (!id) return;
      const initialLength = state.data.length;
      state.data = state.data.filter((v) => (v?._id || v?.id) !== id);
      if (state.data.length < initialLength && state.pagination.total > 0) {
        state.pagination.total -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserTrafficList.pending, (state) => {
        state.loading = state.lastFetchedAt == null;
        state.error = null;
      })
      .addCase(getUserTrafficList.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.error = null;
        state.lastFetchedAt = Date.now();
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(getUserTrafficList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetUserTrafficState, upsertVisitor, removeVisitor } = userTrafficSlice.actions;
export default userTrafficSlice.reducer;